import { create, tsx } from '@dojo/framework/core/vdom';
import { RenderResult } from '@dojo/framework/core/interfaces';

import Text from './Text';
import Summary, { SummaryProperties } from './Summary';

import * as css from './SummaryList.m.css';

export interface SummaryListProperties {
	itemPaddingBottom?: SummaryProperties['paddingBottom'];
	style?: 'leading-image' | 'leading-blurb' | 'alternating' | 'alternating-reverse' | 'stack';
	/** small = 50%/50% columns; medium = 43.232%/56.768%; large = 20%/80% - default large */
	trailingBias?: SummaryProperties['trailingBias'];
	/** adds guttering around the list of summary items, default: md */
	listGuttering?: 'none' | 'md';
}

export interface SummaryListItem {
	image?: RenderResult;
	title?: RenderResult | (() => RenderResult);
	blurb: RenderResult;
}

export interface SummaryListChildren {
	title?: RenderResult;
	blurb?: RenderResult;
	items: (SummaryListItem | ((isOdd: boolean) => RenderResult))[];
}

const factory = create().properties<SummaryListProperties>().children<SummaryListChildren>();

export const SummaryList = factory(function SummaryList({ children, properties }) {
	const {
		itemPaddingBottom = 'xs',
		style,
		trailingBias = 'large',
		listGuttering = 'md'
	} = properties();
	const [{ title, blurb, items }] = children();

	const hasTitle = !!title;

	return (
		<virtual>
			{hasTitle && <Text type="section">{title}</Text>}
			{blurb && (
				<div classes={[css.blurb]}>
					<Text type="body">{blurb}</Text>
				</div>
			)}
			<div
				classes={[
					(hasTitle || !!blurb) && css.itemContainer,
					listGuttering === 'md' && css.listGuttering
				]}
			>
				{items.map((item, index) => {
					const isOdd = !!(index % 2);
					if (typeof item === 'function') {
						return item(isOdd);
					}
					const { title, blurb, image } = item;
					return (
						<Summary
							trailingBias={trailingBias}
							reverse={
								style === 'leading-blurb' ||
								(style === 'alternating-reverse' && !isOdd) ||
								(style === 'alternating' && isOdd)
							}
							stack={style === 'stack'}
							smaller={hasTitle}
							paddingBottom={
								index !== items.length - 1 ? itemPaddingBottom : undefined
							}
						>
							{{
								title,
								blurb,
								image
							}}
						</Summary>
					);
				})}
			</div>
		</virtual>
	);
});

export default SummaryList;
